<template>
  <div class="container">
    <h1 class="page-title">CORE Loader - Upload Chart of Account</h1>

    <p v-if="entity" class="entity-line">
      Uploading into <strong>{{ entity.name }}</strong
      >. Each company keeps its own chart — the same account code means different
      things in each.
    </p>
    <p v-else class="problem-banner">
      This page cannot tell which company it is loading. Set the company on the
      Settings page before uploading, or nothing will be saved.
    </p>

    <div class="toolbar">
      <button
        class="form-button"
        :disabled="downloadingTemplate || !entity"
        @click.prevent="downloadTemplate"
      >
        {{ downloadingTemplate ? "Preparing…" : "Download template" }}
      </button>
      <span class="toolbar-hint">
        The template comes from FiMS pre-filled with the current chart, so you
        extend it rather than retype it.
      </span>
    </div>

    <div class="file-picker">
      <label>Upload the completed template (.xlsx or .csv)</label><br />
      <input type="file" accept=".xlsx,.xls,.csv" @change="handleFileUpload" />
      <p v-if="readingFile">Reading file <span class="loader"></span></p>
    </div>

    <div v-if="problems.length" class="problems">
      <h3>{{ blockingProblems.length ? "This file cannot be uploaded yet" : "Worth checking" }}</h3>
      <ul>
        <li v-for="(problem, index) in problems.slice(0, 25)" :key="index">
          Row {{ problem.row }} — {{ problem.message }}
        </li>
      </ul>
      <p v-if="problems.length > 25">…and {{ problems.length - 25 }} more.</p>
    </div>

    <div v-if="form.length">
      <p class="count-line">{{ form.length }} account(s) read from the file.</p>
      <table class="data-table" id="mytable">
        <thead>
          <tr>
            <th>SN</th>
            <th>Account Number</th>
            <th>Account Name</th>
            <th>Type</th>
            <th>Parent</th>
            <th>Expense Category</th>
            <th>Expense Type</th>
            <th>Department</th>
            <th>Unit</th>
          </tr>
        </thead>
        <tbody :key="tablekey"></tbody>
      </table>
    </div>

    <button
      class="form-button"
      style="background-color: green"
      v-if="!uploadingContent && form.length"
      :disabled="blockingProblems.length > 0 || !entity"
      @click.prevent="storeChart"
    >
      Submit
    </button>
    <p v-if="uploadingContent">uploading <span class="loader"></span></p>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import axios from "axios";
import * as XLSX from "xlsx";
import $ from "jquery";
import "datatables.net";
import Swal from "sweetalert2";
import {
  accountToLegacyPayload,
  expenditureBaseUrl,
  fimsBaseUrl,
  findLocalProblems,
  resolveEntity,
  rowToAccount,
} from "@/fims";

const form = ref([]);
const tablekey = ref(0);
const uploadingContent = ref(false);
const downloadingTemplate = ref(false);
const readingFile = ref(false);
const problems = ref([]);
const url = ref();
const entity = ref(null);

const blockingProblems = computed(() => problems.value.filter((p) => !p.warning));

const failed = (title, text) =>
  Swal.fire({ title, text, icon: "error", confirmButtonText: "Ok" });

const downloadTemplate = async () => {
  downloadingTemplate.value = true;
  try {
    const { data } = await axios.get(`${fimsBaseUrl()}/api/v1/gl/accounts/template`, {
      headers: { "X-Entity-ID": entity.value.id },
      responseType: "blob",
    });
    const href = URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = href;
    link.download = `${entity.value.name}_Chart_of_Accounts_Template.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
  } catch (error) {
    failed("Could not get the template", "FiMS did not return the template. Please try again.");
  } finally {
    downloadingTemplate.value = false;
  }
};

/**
 * Parsed in the browser. The loader's own parser reads the file as text, so it
 * handles CSV but not .xlsx — and the template is an .xlsx because it carries
 * dropdowns and a README that a CSV cannot.
 */
const handleFileUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  readingFile.value = true;
  form.value = [];
  problems.value = [];

  try {
    // raw: true matters. Without it the reader treats a dotted account code as a
    // date — 5.1.0000 comes back as 36647.00040509259 — which would rewrite the
    // chart's codes with no visible sign that anything was wrong.
    const workbook = XLSX.read(await file.arrayBuffer(), {
      type: "array",
      raw: true,
      cellDates: false,
    });
    // The template's first sheet is the README, so go to the grid by name and
    // fall back to the first sheet for a plain CSV.
    const sheetName = workbook.SheetNames.includes("Chart of Accounts")
      ? "Chart of Accounts"
      : workbook.SheetNames[0];
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" });

    const accounts = rows.map(rowToAccount).filter((a) => a.code || a.name);
    if (!accounts.length) {
      failed("Nothing to upload", "No accounts were found in that file.");
      return;
    }

    form.value = accounts;
    problems.value = findLocalProblems(accounts);
    renderTable();
  } catch (error) {
    failed("Could not read that file", "It does not look like a completed template.");
  } finally {
    readingFile.value = false;
  }
};

const renderTable = () => {
  if ($.fn.DataTable.isDataTable("#mytable")) {
    $("#mytable").DataTable().destroy();
  }
  tablekey.value += 1;
  // Let the table re-render before DataTables reads it.
  setTimeout(() => {
    $("#mytable").DataTable({
      paging: true,
      searching: true,
      ordering: true,
      data: form.value,
      columns: [
        { data: null, render: (data, type, row, meta) => meta.row + 1 },
        { data: "code", defaultContent: "" },
        { data: "name", defaultContent: "" },
        { data: "type", defaultContent: "" },
        { data: "parentCode", defaultContent: "" },
        { data: "category", defaultContent: "" },
        { data: "expenditureType", defaultContent: "" },
        { data: "departments", defaultContent: "" },
        { data: "units", defaultContent: "" },
      ],
    });
  }, 0);
};

const storeChart = async () => {
  uploadingContent.value = true;
  try {
    // FiMS first, because it is the ledger and it validates the whole chart
    // atomically — nothing is written unless all of it is valid.
    const { data } = await axios.post(
      `${fimsBaseUrl()}/api/v1/gl/accounts/import`,
      { accounts: form.value, mode: "merge" },
      { headers: { "X-Entity-ID": entity.value.id } }
    );

    // The expenditure system reads the same chart, so it is kept in step from
    // the same upload rather than needing a second one.
    let expenditureWarning = "";
    if (url.value) {
      try {
        await axios.post(
          `${url.value}/api/v1/core/save-services?param=chart_of_account`,
          form.value.map(accountToLegacyPayload)
        );
      } catch (error) {
        expenditureWarning =
          " The expenditure system did not accept its copy, so it may still show the old chart.";
      }
    }

    Swal.fire({
      title: "Uploaded",
      text: (data?.message || "Chart uploaded to FiMS.") + expenditureWarning,
      icon: expenditureWarning ? "warning" : "success",
      confirmButtonText: "Ok",
    });
  } catch (error) {
    // FiMS returns every problem it found, with row numbers, so the whole file
    // can be corrected in one pass.
    const returned = error?.response?.data?.data?.problems;
    if (returned?.length) {
      problems.value = returned.map((p) => ({
        row: p.row ?? "?",
        message: p.message ?? String(p),
      }));
      failed("Nothing was saved", "FiMS rejected the upload. The rows to fix are listed below.");
    } else {
      failed(
        "Nothing was saved",
        error?.response?.data?.message || "FiMS could not be reached."
      );
    }
  } finally {
    uploadingContent.value = false;
  }
};

onMounted(() => {
  entity.value = resolveEntity();
  // Saved settings win; otherwise the expenditure system for this hostname,
  // so a fresh browser works without a trip to the Settings page.
  url.value = expenditureBaseUrl();
});
</script>

<style scoped>
.container {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 10px;
}

.entity-line {
  margin-bottom: 16px;
  color: #333;
}

.problem-banner {
  margin-bottom: 16px;
  padding: 10px;
  border: 1px solid #c00;
  background: #fdeaea;
  color: #900;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.toolbar-hint {
  color: #555;
  font-size: 13px;
}

.file-picker {
  margin-bottom: 20px;
}

.count-line {
  margin: 12px 0;
  color: #333;
}

.problems {
  margin: 16px 0;
  padding: 12px;
  border: 1px solid #e0b400;
  background: #fff8e1;
}

.problems h3 {
  margin: 0 0 8px;
  font-size: 15px;
}

.problems ul {
  margin: 0;
  padding-left: 20px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid black;
}

.data-table th,
.data-table td {
  border: 1px solid black;
  padding: 10px;
  text-align: left;
}

.form-button {
  margin-top: 10px;
  padding: 10px 20px;
  background-color: #007bff;
  color: #fff;
  border: none;
  cursor: pointer;
}

.form-button:disabled {
  background-color: #9e9e9e;
  cursor: not-allowed;
}

.loader {
  width: 10px;
  height: 10px;
  border: 2px solid black;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
